import React, { useEffect, useState } from "react";
import {
  Clock,
  ArrowLeft,
  RefreshCw,
  Mail,
  Home,
  CheckCircle,
} from "lucide-react";
import {
  useNavigate,
  useSearchParams,
  Link,
  useLocation,
} from "react-router-dom";
import { notifyInfo } from "../utils/notify";

const PagamentoPendente: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [paymentData, setPaymentData] = useState<any>(null);

  useEffect(() => {
    // Primeiro, tentar pegar dados do location.state (vindo do PagamentoRetorno)
    if (location.state) {
      setPaymentData(location.state);
      console.log("Dados recebidos via state:", location.state);
      return;
    }

    // Fallback: capturar parâmetros da URL do Mercado Pago (caso acesse diretamente)
    const status = searchParams.get("status");
    const paymentId = searchParams.get("payment_id");
    const preferenceId = searchParams.get("preference_id");
    const externalReference = searchParams.get("external_reference");

    setPaymentData({
      status,
      paymentId,
      preferenceId,
      externalReference,
    });

    // Log para debug
    console.log("Pagamento pendente:", {
      status,
      paymentId,
      preferenceId,
      externalReference,
    });
  }, [searchParams, location.state]);

  const handleVoltarInicio = () => {
    navigate("/");
  };

  const handleVerificarStatus = () => {
    // Aqui você pode implementar uma verificação do status do pagamento
    notifyInfo("Verificando status do pagamento...");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        {/* Header de Pendente */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-yellow-100 rounded-full mb-6">
            <Clock className="w-12 h-12 text-yellow-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            ⏳ Pagamento Pendente
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Seu pagamento está sendo processado
          </p>
          <p className="text-sm text-gray-500">
            Aguarde a confirmação para acessar o sistema
          </p>
        </div>

        {/* Card Principal */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center mb-6">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
            <div className="ml-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Processando Pagamento
              </h2>
              <p className="text-gray-600">Aguarde a confirmação</p>
            </div>
          </div>

          {paymentData && (
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                Detalhes da Transação
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-sm capitalize">
                      {paymentData.status}
                    </span>
                  </div>
                  {paymentData.paymentId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">ID do Pagamento:</span>
                      <span className="font-medium text-gray-900 text-sm">
                        {paymentData.paymentId}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {paymentData.preferenceId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Referência:</span>
                      <span className="font-medium text-gray-900 text-sm">
                        {paymentData.preferenceId}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data:</span>
                    <span className="font-medium text-gray-900 text-sm">
                      {new Date().toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* O que acontece agora */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />O que acontece agora?
            </h3>
            <ul className="space-y-2 text-blue-800">
              <li className="flex items-center">
                <Clock className="w-4 h-4 text-blue-600 mr-2" />
                Seu pagamento está sendo analisado
              </li>
              <li className="flex items-center">
                <Mail className="w-4 h-4 text-blue-600 mr-2" />
                Você receberá um e-mail quando for aprovado
              </li>
              <li className="flex items-center">
                <Clock className="w-4 h-4 text-blue-600 mr-2" />O processo pode
                levar alguns minutos
              </li>
              <li className="flex items-center">
                <RefreshCw className="w-4 h-4 text-blue-600 mr-2" />
                Você pode verificar o status a qualquer momento
              </li>
            </ul>
          </div>

          {/* Ações */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={handleVerificarStatus}
              className="flex justify-center items-center px-6 py-4 border border-transparent text-base font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Verificar Status
            </button>

            <Link
              to="/"
              className="flex justify-center items-center px-6 py-4 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Voltar ao Início
            </Link>
          </div>
        </div>

        {/* Informações Adicionais */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-start">
            <Mail className="w-6 h-6 text-blue-600 mr-3 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Notificações por E-mail
              </h3>
              <p className="text-gray-600 mb-4">
                Você receberá notificações automáticas sobre o status do seu
                pagamento. Verifique sua caixa de entrada e spam.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>Dica:</strong> Assim que o pagamento for aprovado,
                  você receberá acesso completo ao sistema automaticamente.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PagamentoPendente;
